import { useState } from "react";
import { Button } from "primereact/button";
import { TotemTable } from "./components/TotemTable";
import CRUDTotemModal from "./CRUDTotemModal";
import { ETypeOfNotification, Modal, setNotification } from "medere-ui-framework";
import { removeTotem, saveTotem } from "../../../../servicesHooks/Location/useLocationConfigService";
import { Totem } from "../../../../interface/interface";
import { useDispatch } from "react-redux";
import { useLocationConfigContext } from "../../context/LocationConfigContext";
import useResizeListener from "../../../../hooks/useResizeListener";

export default function TotemView() {
    const isMobile = useResizeListener(768);
    const { totems, setTotems, setTotemList, hasDuplicateAssociatedHospitalLocation } = useLocationConfigContext();
    const dispatch = useDispatch()

    const [openModal, setOpenModal] = useState(false);
    const [editTotem, setEditTotem] = useState<Totem | null>(null);

    const [openRemoveModal, setOpenRemoveModal] = useState(false);

    const updateTotems = (newTotems: Totem[]) => { //Actualiza la lista de totems
        setTotems(newTotems) //al auxiliar traido (newTotems) lo actualizo
    };

    const handleCreate = () => { //Crea el totem, en este caso si aparece habilitado el Combobox de Establecimientos
        setEditTotem(null);
        setOpenModal(true);
    };

    const handleEdit = (totem: Totem) => { //Edita un totem en especifico, el cual se precargan sus datos en la modal. En este caso no aparece habilitado el Combobox de Establecimientos pues ya se sabe
        setEditTotem(totem);
        setOpenModal(true);
    };

    const handleOpenRemoveModal = (totem: Totem) => {
        setEditTotem(totem)
        setOpenRemoveModal(true)
    }
    const handleDelete = (totem: Totem) => { //Elimina el totem
        removeTotem(totem.totem)
        .then(() => {
            dispatch(setNotification({ title: "Eliminación exitosa", desc: 'Se ha eliminado el tótem', icon: 'pi-check-circle', typeOfNotification: ETypeOfNotification.INFO }));
            setTotemList();

            setEditTotem(null)
            setOpenRemoveModal(false)
        }).catch((err) => {
            console.error(err)
            if (err.response?.status === 409) {
                if (err.response?.data === 'ASSOCIATED')
                    dispatch(setNotification({ title: "No se puede eliminar", desc: "No se puede eliminar el tótem debido a que ya tiene elementos asociados", icon: 'pi-exclamation-triangle', typeOfNotification: ETypeOfNotification.WARNING }));
            } else
                dispatch(setNotification({ title: "Ocurrió un error", desc: `No se pudo eliminar el tótem`, icon: 'pi-times', typeOfNotification: ETypeOfNotification.ERROR }));
        })
    };

    function areLocationListsDifferent(a: any[], b: any[]): boolean {
        if (a?.length !== b?.length) return true;
        for (let i = 0; i < a?.length; i++) {
            const aItem = a[i];
            const bItem = b[i];
            if (JSON.stringify(aItem) !== JSON.stringify(bItem)) return true;
        }
        return false;
    }

    const handleSave = (newTotem: Totem) => { //Se ejecuta al confirmar los datos del totem centinela de la CRUDModal
        const totemIndex = totems.findIndex(t => t.totem === newTotem.totem);

        let updatedTotems: Totem[];
        
        saveTotem(newTotem).then((res: Totem) => {
            res.establishment = res?.locationdtolist?.length > 0 ? res?.locationdtolist[0]?.building : -1  //Le asigno los campos lógicos del front
            res.hasWaitingQueue = newTotem.hasWaitingQueue

            if (totemIndex !== -1) { //Si existe (estaba editando), actualizo el totem
                updatedTotems = [...totems];
                updatedTotems[totemIndex] = res;
            } else { //Si no existe, añado el totem nuevo
                updatedTotems = [...totems, res]
            }

            updateTotems(updatedTotems)
            dispatch(setNotification({ title: (editTotem && editTotem.totem > 0) ? "Edición exitosa":"Creación exitosa", desc: (editTotem && editTotem.totem > 0) ? 'Se ha editado el tótem':'Se ha creado el tótem', icon: 'pi-check-circle', typeOfNotification: ETypeOfNotification.INFO }));
            
            setTimeout(() => { //Para que no refresque de inmediato
                if (areLocationListsDifferent(editTotem?.locationdtolist, newTotem?.locationdtolist)) {
                    window.location.reload();
                }
            }, 2000); //Recargo para que al haber asociado puestos de atencion, si el asociado al dispositivo se agrego a otro totem o se cambio de totem, se debe refrezcar los queuenumbers recibidos
            
            setEditTotem(null)
            setOpenModal(false);
        }).catch((err) => {
            console.error("Error al guardar el tótem: ", err);
            if (err.response?.status === 409) {
                if (hasDuplicateAssociatedHospitalLocation(newTotem))
                    dispatch(setNotification({ title: "No se puede guardar", desc: "No se pueden repetir los puestos de atención asociados.", icon: 'pi-exclamation-triangle', typeOfNotification: ETypeOfNotification.WARNING }));
                else if (totems.some(t => t.totemid === newTotem.totemid && t.totem !== newTotem.totem)) //Recién acá confirmamos que el conflicto es realmente por ID duplicado
                    dispatch(setNotification({ title: "No se puede guardar", desc: "La ID ya está siendo usada por otro tótem.", icon: 'pi-exclamation-triangle', typeOfNotification: ETypeOfNotification.WARNING }));
                else
                    dispatch(setNotification({ title: "No se puede guardar", desc: "Revisá los datos ingresados: puede que algún texto supere la longitud permitida.", icon: 'pi-exclamation-triangle', typeOfNotification: ETypeOfNotification.WARNING }));
            } else if (err.response?.status === 422) {
                dispatch(setNotification({ title: "No se puede guardar", desc: "Este tótem necesita al menos un puesto de atención asociado (recordá clickear el botón + para agregarlo a la lista).", icon: 'pi-exclamation-triangle', typeOfNotification: ETypeOfNotification.WARNING }));
            } else
                dispatch(setNotification({ title: "Ocurrió un error", desc: `No se pudo guardar el tótem`, icon: 'pi-times', typeOfNotification: ETypeOfNotification.ERROR }));
        })
    };

    return (
        <div>
            <div className={`emr-border-bottom ${isMobile ? "location-mobile-top-toolbar" : "flex flex-row justify-content-between"}`}>
                <h1 className="title2">Tótem de registro de atención</h1>
                <Button
                    id="createtotembuttonid"
                    icon="pi pi-plus-circle"
                    className={`buttonMain ${isMobile ? "location-mobile-create-btn" : "h-9"}`}
                    label="Crear"
                    onClick={handleCreate}
                />
            </div>

            <TotemTable
                totems={totems}
                onChange={(newTotems) => updateTotems(newTotems)}
                onEdit={(totem) => handleEdit(totem)}
                onDelete={(totem) => handleOpenRemoveModal(totem)}
            />

            {openModal && (
                <CRUDTotemModal
                    visible={openModal}
                    setVisible={setOpenModal}
                    onSubmit={handleSave}
                    totemToEdit={editTotem ?? undefined}
                />
            )}

            <Modal className="location-mobile-editor-modal" visible={openRemoveModal} setVisible={setOpenRemoveModal} header={"Estás por eliminar el tótem"} headerStyle={{ fontSize: '12px', textAlign: 'center' }} width='w-medium' closable={false}>
                <div>
                    <p className="grey-color">No podrás eliminar tótems que tengan elementos asociados.</p>
                    <div style={{ marginTop: '1rem', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center'}}>
                        <Button id="deletebuttonid" label="Si, eliminar" onClick={() => handleDelete(editTotem)} className="w-full buttonMain" />
                        <Button id="canceldeletebuttonid" label={"Cancelar"} onClick={() => setOpenRemoveModal(false)} className="w-full btnCancel2" />
                    </div>
                </div>
            </Modal>
        </div>
    );
}

